import { XCircle, Hourglass } from 'lucide-react';
import type { ReactNode } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { cn } from '@/lib/utils';

type StatusTileVariant = 'waiting' | 'error';

interface StatusTileProps {
  title: string;
  message?: ReactNode;
  variant?: StatusTileVariant;
  /** Optional actions (e.g. logout button) shown below the message */
  actions?: ReactNode;
  className?: string;
}

/**
 * Centered status tile for waiting/loading or error states.
 * Matches the style of KUNDENPORTAL/StatusTile.vue with icon and message.
 */
export function StatusTile({ title, message, variant = 'waiting', actions, className }: StatusTileProps) {
  const isError = variant === 'error';
  const Icon = isError ? XCircle : Hourglass;
  const iconColorClass = isError ? 'text-destructive' : 'text-primary';
  const titleColorClass = isError ? 'text-destructive' : 'text-primary';

  return (
    <div className={cn('fixed inset-0 z-50 flex items-center justify-center p-6', className)}>
      <Card
        as='div'
        className='flex w-full max-w-[90vw] flex-col border shadow-sm md:max-w-[42.5rem] md:flex-row md:px-8 md:py-8'
      >
        <CardContent className='flex flex-col items-center gap-6 p-6 md:flex-row md:items-start md:gap-10 md:py-0 md:pt-6'>
          <div className='shrink-0 md:mb-0'>
            <Icon className={cn('size-[6.5rem]', iconColorClass)} strokeWidth={1.5} aria-hidden />
          </div>
          <div className='min-w-0 flex-1 text-center md:text-left'>
            <h1 className={cn('text-h3 mb-2', titleColorClass)}>{title}</h1>
            {message != null && <p className='text-body-1 font-medium m-0'>{message}</p>}
            {actions != null && <div className='mt-6 md:mt-8'>{actions}</div>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
